
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { MdArrowBack, MdSave, MdEdit } from 'react-icons/md';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import './AddProject.css';
import Swal from 'sweetalert2';
import Select from 'react-select';
import axios from 'axios';
import {
    UPDATE_PROJECT_API,
    VIEW_PROJECT_API,
    GET_ALL_CUSTOMERS_API,
    GET_ALL_VENDORS_API,
    GET_ALL_PROJECT_CATEGORIES_API,
    GET_ALL_PROJECT_SUB_CATEGORIES_API,
    GET_ALL_PROJECT_SUB_CATEGORY_DETAILS_API
} from '../config/API';
import Loader from '../components/common/Loader';
import { showErrorToast } from '../utils/toastUtils';
import PaginatedSelect from '../components/common/PaginatedSelect';


const EditProject = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Detect if we are in 'view' mode (from path or state)
    const [isViewMode, setIsViewMode] = useState(
        location.pathname.includes('/view/') || location.state?.mode === 'view'
    );
    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(true);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedVendors, setSelectedVendors] = useState([]);

    // New dropdown states
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubCategory, setSelectedSubCategory] = useState(null);
    const [selectedDetail, setSelectedDetail] = useState(null);

    const [formData, setFormData] = useState({
        projectName: '',
        projectAdd: '',
        googleMapLink: '',
        startDate: null,
        endDate: null,
        description: '',
        status: 'Active'
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                setFetchingData(true);
                const token = localStorage.getItem('accessToken');
                const headers = { Authorization: `Bearer ${token}` };

                // 1. Always Fetch Fresh Project Data from API
                const projResponse = await axios.get(VIEW_PROJECT_API(id), { headers });

                if (projResponse.data.status) {
                    const p = projResponse.data.data;
                    setFormData({
                        projectName: p.projectName || '',
                        projectAdd: p.projectAdd || '',
                        googleMapLink: p.googleMapLink || '',
                        startDate: p.projectStartDate ? new Date(p.projectStartDate) : (p.startDate ? new Date(p.startDate) : null),
                        endDate: p.projectEndDate ? new Date(p.projectEndDate) : (p.endDate ? new Date(p.endDate) : null),
                        description: p.Description || p.description || '',
                        status: (p.status || 'Active')
                    });

                    // Customer mapping
                    if (p.Customer || p.customer) {
                        const cust = p.Customer || p.customer;
                        setSelectedCustomer({ value: cust.id || cust._id, label: cust.customerName });
                    } else if (p.customerId) {
                        setSelectedCustomer({ value: p.customerId, label: p.customerName || 'Loading...' });
                    }

                    // Vendor mapping
                    if (p.ProjectVendors && Array.isArray(p.ProjectVendors)) {
                        setSelectedVendors(p.ProjectVendors.map(pv => ({
                            value: pv.Vendor?.id || pv.Vendor?._id,
                            label: pv.Vendor?.category
                                ? `${pv.Vendor.companyName} (${pv.Vendor.category})`
                                : pv.Vendor?.companyName
                        })));
                    } else if (p.vendors && Array.isArray(p.vendors)) {
                        setSelectedVendors(p.vendors.map(v => ({
                            value: v.id || v._id,
                            label: v.companyName + " " + (v.category) || 'Loading...'
                        })));
                    }

                    // Category mapping
                    const cat = p.ProjectCategory || p.projectCategory;
                    if (cat) {
                        setSelectedCategory({ value: cat.id || cat._id, label: cat.categoryName });
                    }

                    const subCat = p.ProjectSubCategory || p.projectSubCategory;
                    if (subCat) {
                        setSelectedSubCategory({ value: subCat.id || subCat._id, label: subCat.categoryName || subCat.subCategoryName });
                    }

                    const detail = p.ProjectTypeSubCategory || p.projectTypeSubCategoryDetail;
                    if (detail) {
                        setSelectedDetail({ value: detail.id || detail._id, label: detail.detailName || detail.name });
                    }
                } else {
                    showErrorToast('Failed to fetch project details');
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                showErrorToast('Error loading project data');
            } finally {
                setFetchingData(false);
            }
        };

        fetchData();
    }, [id]);

    const handleCustomerChange = (selectedOption) => {
        setSelectedCustomer(selectedOption);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDateChange = (date, field) => {
        setFormData(prev => ({
            ...prev,
            [field]: date
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isViewMode) return;


        const result = await Swal.fire({
            title: 'Update Project?',
            text: 'Do you want to save the changes to this project?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Update',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#4f46e5',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-confirm-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const token = localStorage.getItem('accessToken');

                const payload = {
                    ...formData,
                    customerId: selectedCustomer?.value || '',
                    vendorIds: selectedVendors.map(v => v.value),
                    projectCatId: selectedCategory?.value || null,
                    projectSubCatId: selectedSubCategory?.value || null,
                    projectSubCatDetailId: selectedDetail?.value || null,
                    startDate: formData.startDate ? formData.startDate.toISOString().split('T')[0] : null,
                    endDate: formData.endDate ? formData.endDate.toISOString().split('T')[0] : null
                };

                const response = await axios.put(UPDATE_PROJECT_API(id), payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Updated!',
                        text: 'Project updated successfully',
                        timer: 2000,
                        showConfirmButton: false,
                    });
                    navigate('/projects');
                } else {
                    showErrorToast(response.data.error || response.data.message || 'Failed to update project');
                }
            } catch (error) {
                console.error('Error updating project:', error);
                const errorData = error.response?.data?.error || error.response?.data?.message || 'An error occurred';
                showErrorToast(errorData);
            } finally {
                setLoading(false);
            }
        }
    };

    if (loading) {
        return <Loader fullPage text="Updating project..." />;
    }

    return (
        <div className="add-project-page">
            <div className="page-header">
                <button onClick={() => navigate('/projects')} className="back-btn">
                    <MdArrowBack />
                </button>
                <h2>{isViewMode ? 'Project Details' : 'Edit Project'}</h2>
                {isViewMode && (
                    <button onClick={() => setIsViewMode(false)} className="edit-toggle-btn" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                        <MdEdit /> Edit Mode
                    </button>
                )}
            </div>

            <form onSubmit={handleSubmit} className="project-form">
                <div className="form-section">
                    <h3>Customer Details</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Select Customer <span className="required-star">*</span></label>
                            <PaginatedSelect
                                apiUrl={GET_ALL_CUSTOMERS_API}
                                mapResponse={(cust) => ({ value: cust.id || cust._id, label: cust.customerName })}
                                onChange={handleCustomerChange}
                                value={selectedCustomer}
                                placeholder="-- Select a Customer --"
                                isClearable
                                isDisabled={isViewMode}
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        </div>
                        <div class="form-group">
                            <label>Select Vendor(s)</label>
                            <PaginatedSelect
                                apiUrl={GET_ALL_VENDORS_API}
                                mapResponse={(v) => ({
                                    value: v.id || v._id,
                                    label: v.category
                                        ? `${v.companyName} (${v.category})`
                                        : v.companyName
                                })}
                                onChange={(selected) => setSelectedVendors(selected || [])}
                                value={selectedVendors}
                                placeholder="-- Select Vendor(s) --"
                                isMulti
                                isClearable
                                isDisabled={isViewMode}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h3>Project Details</h3>
                    <div className="form-grid">
                        <div className="form-group full-width">
                            <label>Project Name <span className="required-star">*</span></label>
                            <input
                                type="text"
                                name="projectName"
                                value={formData.projectName}
                                onChange={handleChange}
                                placeholder="Enter project name"
                                disabled={isViewMode}
                            />
                        </div>
                        <div className="form-group">
                            <label>Project Type Category</label>
                            <PaginatedSelect
                                apiUrl={GET_ALL_PROJECT_CATEGORIES_API}
                                mapResponse={(item) => ({ value: item.id || item._id, label: item.categoryName })}
                                onChange={setSelectedCategory}
                                value={selectedCategory}
                                placeholder="Select Category"
                                isClearable
                                isDisabled={isViewMode}
                            />
                        </div>
                        <div className="form-group">
                            <label>Project Type Sub-Category</label>
                            <PaginatedSelect
                                apiUrl={GET_ALL_PROJECT_SUB_CATEGORIES_API}
                                mapResponse={(item) => ({ value: item.id || item._id, label: item.categoryName || item.subCategoryName })}
                                onChange={(selected) => {
                                    setSelectedSubCategory(selected);
                                    if (selected?.label?.toLowerCase() !== 'apartment') {
                                        setSelectedDetail(null);
                                    }
                                }}
                                value={selectedSubCategory}
                                placeholder="Select Sub-Category"
                                isClearable
                                isDisabled={isViewMode}
                            />
                        </div>
                        {selectedSubCategory?.label?.toLowerCase() === 'apartment' && (
                            <div className="form-group">
                                <label>BHK (Project Type Sub-Category Detail)</label>
                                <PaginatedSelect
                                    apiUrl={GET_ALL_PROJECT_SUB_CATEGORY_DETAILS_API}
                                    mapResponse={(item) => ({ value: item.id || item._id, label: item.detailName || item.detail_name || item.name })}
                                    onChange={setSelectedDetail}
                                    value={selectedDetail}
                                    placeholder="Select BHK / Detail"
                                    isClearable
                                    isDisabled={isViewMode}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />
                            </div>
                        )}

                        <div className="form-group full-width">
                            <label>Project Address / Location <span className="required-star">*</span></label>
                            <input
                                type="text"
                                name="projectAdd"
                                value={formData.projectAdd}
                                onChange={handleChange}
                                placeholder="Enter project location"
                                disabled={isViewMode}
                            />
                        </div>
                        <div className="form-group full-width">
                            <label>Google Map Link</label>
                            <input
                                type="text"
                                name="googleMapLink"
                                value={formData.googleMapLink}
                                onChange={handleChange}
                                placeholder="Enter Google Map Link"
                                disabled={isViewMode}
                            />
                        </div>
                        <div className="form-group full-width">
                            <label>Description</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows="4"
                                placeholder="Enter project description"
                                disabled={isViewMode}
                            ></textarea>
                        </div>
                        <div className="form-group">
                            <label>Start Date</label>
                            <DatePicker
                                selected={formData.startDate}
                                onChange={(date) => handleDateChange(date, 'startDate')}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                placeholderText="Select start date"
                                isClearable
                                disabled={isViewMode}
                            />
                        </div>
                        <div className="form-group">
                            <label>End Date</label>
                            <DatePicker
                                selected={formData.endDate}
                                onChange={(date) => handleDateChange(date, 'endDate')}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                placeholderText="Select end date"
                                isClearable
                                disabled={isViewMode}
                            />
                        </div>
                        <div className="form-group">
                            <label>Status <span className="required-star">*</span></label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                disabled={isViewMode}
                            >
                                <option value="Active">Active</option>
                                <option value="Completed">Completed</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" onClick={() => navigate('/projects')} className="cancel-btn">
                        {isViewMode ? 'Back to List' : 'Cancel'}
                    </button>
                    {!isViewMode && (
                        <button type="submit" className="save-btn">
                            <MdSave /> Update Project
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default EditProject;
